import React from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Box, Divider, IconButton, Paper, Typography } from "@mui/material";
import DeleteIcon from "@icons/myicons/trash-bin-delete.svg?react";
import { TransactionValidationError } from "@api";
import { toRoundedNumberOfLots, toCurrency } from "@helpers";
import { formatDateOrString } from "@helpers/date/dateFormat";
import { ActivityWithBooking } from "../helpers/bookingLocator";
import { ParsingResultActivitiesError } from "./ParsingResultActivitiesError";

const Container = styled(Paper)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(4)};
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const NumberContainer = styled.div`
  margin-right: ${({ theme }) => theme.spacing(1)};
`;

const AmountContainer = styled.div`
  margin-left: auto;
  padding: ${({ theme }) => theme.spacing(0.7)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const DateContainer = styled.div`
  margin-left: auto;
`;

const TypeWithNameContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  & > :not(style) {
    margin-right: ${({ theme }) => theme.spacing(2)};
  }
`;

const TypeIcon = styled.div`
  background-color: ${({ theme }) => theme.palette.background.selected};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  padding: 2px 4px;
`;

export interface Props {
  transactionValidationErrors: TransactionValidationError[];
  activitiesWithBooking: ActivityWithBooking[];
  testid: string;
  onRemove: (activityWithBooking: ActivityWithBooking) => void;
}

export const ParsingResultActivities = ({
  activitiesWithBooking,
  transactionValidationErrors,
  onRemove,
  testid,
}: Props) => {
  const { t } = useTranslation();
  const hasError = transactionValidationErrors?.length > 0;

  return (
    <Container data-testid={testid || "parsingResult-activities"}>
      {activitiesWithBooking.map((activityWithBooking, index) => (
        <Box
          key={activityWithBooking.activity.uuid}
          data-testid={`${testid}-activity-${index}`}
        >
          {index !== 0 && (
            <Divider sx={{ marginTop: "6px", marginBottom: "6px" }} />
          )}
          <FlexRow>
            <TypeWithNameContainer>
              {!activityWithBooking.booking && (
                <TypeIcon>{t("transactionUpload.newTransaction")}</TypeIcon>
              )}
              {activityWithBooking.booking && (
                <TypeIcon>{t("transactionUpload.updateTransaction")}</TypeIcon>
              )}

              <NumberContainer>
                <Typography variant="body1">
                  {t(
                    `transactionUpload.transactionTypes.${activityWithBooking.activity.type}`
                  )}
                </Typography>
              </NumberContainer>
            </TypeWithNameContainer>

            {activityWithBooking.activity.numberOfLots && (
              <NumberContainer>
                <Typography variant="body1">
                  {toRoundedNumberOfLots(
                    activityWithBooking.activity.numberOfLots,
                    6
                  )}
                </Typography>
              </NumberContainer>
            )}
            {hasError &&
              transactionValidationErrors
                .filter((error) => error.hash === activityWithBooking?.hash)
                .map((error, ix) => {
                  return (
                    <ParsingResultActivitiesError
                      data-testid={`activity-with-booking-error-${ix}`}
                      message={error.errorMessage}
                    />
                  );
                })}
            {activityWithBooking.activity.netAmount && (
              <AmountContainer>
                <Typography variant="body2">
                  {toCurrency(activityWithBooking.activity.netAmount)}
                </Typography>
              </AmountContainer>
            )}
          </FlexRow>

          <div>
            <TypeWithNameContainer>
              {!activityWithBooking.investment?.id && (
                <TypeIcon>{t("transactionUpload.newInvestment")}</TypeIcon>
              )}
              <Typography variant="body1">
                <b>
                  {activityWithBooking?.activity.name ||
                    activityWithBooking?.activity.cryptoSymbol}
                </b>
                {activityWithBooking.activity.isin &&
                  ` (${activityWithBooking.activity.isin})`}
              </Typography>
            </TypeWithNameContainer>
          </div>

          {!!activityWithBooking.activity.taxAmount && (
            <Typography variant="body3">
              {toCurrency(activityWithBooking.activity.taxAmount)}{" "}
              {t("transactionUpload.tax")}
            </Typography>
          )}

          {!!activityWithBooking.activity.commission && (
            <Typography variant="body3">
              {toCurrency(activityWithBooking.activity.commission)}{" "}
              {t("transactionUpload.fee")}
            </Typography>
          )}

          <Box display="flex" alignItems="center" gap={2}>
            {!!activityWithBooking.activity.date && (
              <FlexRow>
                <DateContainer>
                  <Typography variant="body3">
                    {formatDateOrString(activityWithBooking.activity.date)}
                  </Typography>
                </DateContainer>
              </FlexRow>
            )}
            <IconButton
              size="small"
              onClick={() => {
                onRemove(activityWithBooking);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      ))}
    </Container>
  );
};
